import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Typography } from "@mui/material";
import { Button, IconButton, Stack } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";

// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 * 
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 * 
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 *    Array of objects with productId and quantity of products in cart
 * 
 * @param { Array.<Product> } productsData
 *    Array of objects with complete data on all available products
 *
 * @returns { Array.<CartItem> }
 *    Array of objects with complete data on products in cart
 *
 */
export const generateCartItemsFrom = (cartData, productsData) => {
  let cartItems=[];
  for(let i=0;i<cartData.length;i++){
    for(let j=0;j<productsData.length;j++){
      if(cartData[i].productId===productsData[j]._id){
        cartItems.push({...productsData[j],...cartData[i]});
      }
    }
  }
  if(productsData.length===0){return cartData;}
  return cartItems;
};

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products added to the cart
 *
 * @returns { Number }
 *    Value of all items in the cart
 *
 */
export const getTotalCartValue = (items = []) => {
  let total=0;
  for(let i=0;i<items.length;i++){
    total+=(items[i].qty*items[i].cost);
  }
  return total;
};


// TODO: CRIO_TASK_MODULE_CHECKOUT - Implement function to return total cart quantity
/**
 * Return the sum of quantities of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products in cart
 *
 * @returns { Number }
 *    Total quantity of products added to the cart
 *
 */
export const getTotalItems = (items = []) => {
  let sum=0;
  items.forEach((item)=>sum+=item.qty);
  return sum;
};

// TODO: CRIO_TASK_MODULE_CHECKOUT - Add static quantity view for Checkout page cart
/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 * 
 * @param {Number} value
 *    Current quantity of product in cart
 * 
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 * 
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 * 
 * @param {Boolean} isReadOnly
 *    If product quantity on cart is to be displayed as read only without the + - options to change quantity
 * 
 */
const ItemQuantity = ({
  value,
  handleAdd,
  handleDelete,
  isReadOnly
}) => {
  return (
    <Stack direction="row" alignItems="center">
      {isReadOnly?<Box padding="0.5rem" data-testid="item-qty">
        Qty: {value}
      </Box>:<><IconButton size="small" color="primary" onClick={handleDelete}>
        <RemoveOutlined />
      </IconButton>
      <Box padding="0.5rem" data-testid="item-qty">
        {value}
      </Box>
      <IconButton size="small" color="primary" onClick={handleAdd}>
        <AddOutlined />
      </IconButton></>}
    </Stack>
  );
};

/**
 * Component to display the Cart view
 * 
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 * 
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 * 
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 * 
 * @param {Boolean} isReadOnly
 *    If product quantity on cart is to be displayed as read only without the + - options to change quantity
 * 
 */
const Cart = ({
  products,
  items = [],
  handleQuantity,
  isReadOnly
}) => {
  let history=useHistory();
  if (!items.length) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }
  return (
    <>
      <Box className="cart">
        {/* TODO: CRIO_TASK_MODULE_CART - Display view for each cart item with non-zero quantity */}
        {products.map((prod)=>(
          <Box display="flex" alignItems="flex-start" padding="1rem" key={prod._id}>
    <Box className="image-container">
        <img
            // Add product image
            src={prod.image}
            // Add product name as alt eext
            alt={prod.name}
            width="100%"
            height="100%"
        />
    </Box>
    <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="6rem"
        paddingX="1rem"
    >
        <div>{prod.name}</div>
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
        >
        {isReadOnly?<ItemQuantity
        value={prod.qty}
        handleAdd={(e)=>{handleQuantity.handleAdd(prod._id,prod.qty)}}
        handleDelete={(e)=>{handleQuantity.handleSub(prod._id,prod.qty)}}
        isReadOnly
        />:<ItemQuantity
        // Add required props by checking implementation
        value={prod.qty}
        handleAdd={(e)=>{handleQuantity.handleAdd(prod._id,prod.qty)}}
        handleDelete={(e)=>{handleQuantity.handleSub(prod._id,prod.qty)}}
        />}
        <Box padding="0.5rem" fontWeight="700">
            ${prod.cost}
        </Box>
        </Box>
    </Box>
</Box>
        ))}
        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Order total
          </Box>
          <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            ${getTotalCartValue(items)}
          </Box>
        </Box>

        {isReadOnly?<></>:<Box display="flex" justifyContent="flex-end" className="cart-footer">
          <Button
            color="primary"
            variant="contained"
            startIcon={<ShoppingCart />}
            className="checkout-btn"
            onClick={()=>{history.push("/checkout")}}>
            Checkout
          </Button>
        </Box>}
        </Box>
        <Box>
        {isReadOnly?<><Box padding="1rem" className="cart">
        <h3>Order Details</h3>
        <Box display='flex' justifyContent="space-between">
        <Typography>Products</Typography>
        <Typography>${getTotalItems(items)}</Typography>
        </Box>
        <Box display='flex' justifyContent="space-between">
        <Typography>Subtotal</Typography>
        <Typography>${getTotalCartValue(items)}</Typography>
        </Box>
        <Box display='flex' justifyContent="space-between">
        <Typography>Shipping</Typography>
        <Typography>$0</Typography>
        </Box>
        <Box display='flex' justifyContent="space-between">
        <Typography fontWeight="900">Total</Typography>
        <Typography fontWeight="900">${getTotalCartValue(items)}</Typography>
        </Box>
        </Box></>:<></>}
      </Box>
    </>
  );
};

export default Cart;
